import { Component, ElementRef, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { NavigationService } from 'src/app/services/navigation.service';
import { environment } from 'src/environments/environment';

/**
 * A component that displays a loading spinner with a logo.
 * The logo appearance changes based on the background (light/dark).
 * The component's visibility is controlled by the NavigationService.
 *
 * @example
 * <!-- For dark background -->
 * <loading class="loading"></loading>
 *
 * @example
 * <!-- For light background -->
 * <loading></loading>
 */
@Component({
  selector: 'loading',
  templateUrl: './loading.component.html',
  styleUrls: ['./loading.component.scss'],
})
export class LoadingComponent implements OnDestroy {
  /** The URL of the loading logo GIF */
  public logo!: string;

  /** Subscription to handle the loading state */
  private loadingSubscription: Subscription;

  constructor(
    private navigationService: NavigationService,
    private elementRef: ElementRef<HTMLDivElement>,
  ) {
    // Determine which logo to use based on the presence of the 'loading' class
    const loading_class = this.elementRef.nativeElement.classList[0];
    this.setLogoBasedOnBackground(loading_class);

    // Subscribe to loading state changes
    this.loadingSubscription = this.navigationService.fullScreenLoaderVisible$.subscribe({
      next: (loading) => {
        this.updateVisibility(loading);
      },
      error: (error) => {
        console.error('Error in loading subscription:', error);
        // Ensure loader is hidden in case of error
        this.updateVisibility(false);
      },
    });

    // Preload the logo image to prevent flash
    this.preloadLogo();
  }

  /**
   * Sets the appropriate logo URL based on the background type
   * @param loading_class - The first class of the element
   */
  private setLogoBasedOnBackground(loading_class: string | undefined): void {
    const baseUrl = `${environment.google.bucket}/assets/agingplan/loading`;
    this.logo = loading_class ? `${baseUrl}/WebsiteLoadingLogo_Reflection_DarkBackground.gif` : `${baseUrl}/WebsiteLoadingLogo_Reflection_LightBackground.gif`;
  }

  /**
   * Updates the visibility of the loading component
   * @param visible - Whether the loader should be visible
   */
  private updateVisibility(visible: boolean): void {
    this.elementRef.nativeElement.style.display = visible ? 'flex' : 'none';
  }

  /**
   * Preloads the logo image to prevent flash when showing
   */
  private preloadLogo(): void {
    const img = new Image();
    img.onerror = () => {
      console.error('Failed to load logo:', this.logo);
    };
    img.src = this.logo;
  }

  /**
   * Cleanup subscriptions when the component is destroyed
   */
  ngOnDestroy(): void {
    if (this.loadingSubscription) {
      this.loadingSubscription.unsubscribe();
    }
  }
}
